import React from 'react';
import { Hand, Minus, Plus } from 'lucide-react';
import Badge from '../../../ui/Badge';
import Tooltip from '../../../ui/Tooltip';
import { useLongPress } from '../../../hooks/useLongPress';
import { getPlayerColor } from '../utils/playerUtils';
import { counterIcon } from '../utils/counterIcons';
import { ZONES } from './ZoneTile';
import './ParticipantHead.css';

const LONG_PRESS_STEP = 10;
const ZONE_SYMBOLS = { hand: { label: 'Hand', Icon: Hand }, ...ZONES };
const ZONE_ACTIONS = {
  hand: 'Hand ansehen anfragen',
  deck: 'Bibliothek ansehen',
  graveyard: 'Friedhof ansehen',
  exile: 'Exil ansehen',
};

/**
 * Teilnehmer-Kopf (Spec 2026-10-21 §5.6, Status §13): dieselbe Komponente für den eigenen
 * Teilnehmer und alle anderen.
 *
 *   <ParticipantHead player={p} isOwn isActiveTurn pendingDelta={-3}
 *     onLifeChange={add} onLongLifeChange={add} onOpenValues={…} />
 *   <ParticipantHead player={p} onOpenValues={…} onZoneClick={(zone) => …} onRequestHand={…}
 *     previewBind={hover.bind} />
 *
 * - Punkt in `getPlayerColor(position)` mit der Nummer `position + 1` (1–4), daneben der Name.
 * - Zwei unabhängige Abzeichen: „Ausgeschieden" (`playerStatus === 'eliminated'`, Ton `danger`,
 *   endgültig bis zum Zurücksetzen) und „Abwesend" (`isAway`, Ton `pending`), auch beide zugleich.
 *   Ein eigenes „Tot" gibt es nicht: Leben ≤ 0 setzt serverseitig `eliminated`. In der Chip-Stufe
 *   stehen sie als farbiger Punkt mit dem Wort als Tooltip und `aria-label`
 *   (`participant-status`), damit sie in schmalen Chips nicht abgeschnitten werden.
 * - Am Zug: goldener Rahmen und `data-active-turn`, kein Text.
 * - Rechtsbündig alle Marken mit Wert > 0 als Symbol und Zahl, Name als `ui/Tooltip`.
 * - Zweite Zeile, eigener Kopf: Leben zwischen Minus und Plus (≥ 44 px), Klick ±1 über
 *   `onLifeChange`, Gedrückthalten ±10 über `onLongLifeChange`; darunter die noch nicht
 *   übergebene Differenz `pendingDelta`. Angezeigt wird `player.life` — wer Klicks sammelt,
 *   übergibt dort schon Serverstand plus Differenz. `readOnly` (ausgeschieden): nur die Zahl.
 * - Zweite Zeile, andere: Leben, rechts die Zonen als Symbol und Zahl (Hand, Bibliothek,
 *   Friedhof, Exil nur wenn belegt). Hand → `onRequestHand()` (fehlt er, ist der Chip nur
 *   Anzeige), Friedhof/Exil → `onZoneClick(zone)`. Die Bibliothek ist hervorgehoben, wenn sie
 *   offen gespielt wird; nur dann ist sie bedienbar (`onZoneClick('deck')`) und zeigt ihre
 *   Oberkarte als Vorschau (`previewBind(card)`, Rechner). Die Zahl der Bibliothek kommt aus
 *   `deck` — die Reducer führen das Array bei jedem Ziehen und Bewegen nach.
 * - Tooltips (`ui/Tooltip`): bedienbare Zonen nennen ihre Aktion („Friedhof ansehen", „Hand
 *   ansehen anfragen"; `aria-label` mit Zahl), reine Anzeigen „Zone: Zahl"; Name, Marken und
 *   Plus/Minus tragen ebenfalls einen. In der Chip-Stufe als Ganzes-Knopf sind die inneren
 *   Auslöser nicht fokussierbar — die Tastatur klappt den Kopf auf und erreicht sie dort.
 * - Klick auf die Lebenszahl → `onOpenValues()` (Werte-Aufklapper).
 * - `compact`: einzeilige Chip-Stufe (Punkt, Name, Leben, Symbole). Der eigene Chip behält
 *   Plus/Minus; ein fremder Chip ist als Ganzes ein Knopf für `onExpand()`. `zonesInChip={false}`
 *   lässt die Zonen im Chip weg (enge Zeile; aufgeklappt stehen sie wieder da).
 * - Reicht seine `ref` an das Wurzel-Element weiter (Anker des Werte-Aufklappers).
 * - Prüfanker: `data-testid="participant-head"` mit `data-user-id`, `data-own`,
 *   `data-active-turn`; `participant-number`, `participant-name`, `participant-life`,
 *   `participant-counter`, `participant-zone` (mit `data-zone`), `participant-life-delta`,
 *   `participant-status` (Punkt in der Chip-Stufe).
 */
const ParticipantHead = React.forwardRef(function ParticipantHead({
  player, isOwn = false, isActiveTurn = false, compact = false, zonesInChip = true, pendingDelta = 0, readOnly = false,
  onLifeChange, onLongLifeChange, onOpenValues, onZoneClick, onRequestHand, onExpand, previewBind, onClick, ...rest
}, ref) {
  const name = player.displayName;
  const expandable = compact && !isOwn && Boolean(onExpand);
  // Reine Anzeigen mit Tooltip werden per Tab erreichbar — außer im Ganzes-Knopf-Chip.
  const focusTarget = expandable ? undefined : 0;
  const classes = ['participant-head'];
  if (isOwn) classes.push('participant-head--own');
  if (compact) classes.push('participant-head--compact');

  const handleExpandKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onExpand();
    }
  };

  const life = isOwn && !readOnly ? (
    <OwnLife
      life={player.life}
      name={name}
      pendingDelta={pendingDelta}
      onLifeChange={onLifeChange}
      onLongLifeChange={onLongLifeChange}
      onOpenValues={onOpenValues}
    />
  ) : (
    <LifeNumber life={player.life} name={name} onOpenValues={expandable ? undefined : onOpenValues} />
  );

  const zones = !isOwn && (
    <ZoneChips
      player={player}
      interactive={!expandable}
      focusTarget={focusTarget}
      onZoneClick={onZoneClick}
      onRequestHand={onRequestHand}
      previewBind={previewBind}
    />
  );

  return (
    <div
      {...rest}
      ref={ref}
      className={classes.join(' ')}
      data-testid="participant-head"
      data-user-id={player.userId}
      data-own={isOwn ? 'true' : undefined}
      data-active-turn={isActiveTurn ? 'true' : undefined}
      role={expandable ? 'button' : undefined}
      tabIndex={expandable ? 0 : undefined}
      aria-label={expandable ? `${name} aufklappen` : undefined}
      onClick={expandable ? onExpand : onClick}
      onKeyDown={expandable ? handleExpandKey : undefined}
    >
      <div className="participant-head__top">
        <span
          className="participant-head__number"
          style={{ '--seat-color': getPlayerColor(player.position) }}
          data-testid="participant-number"
          aria-hidden
        >
          {player.position + 1}
        </span>
        <Tooltip label={name}>
          <span className="participant-head__name" tabIndex={focusTarget} data-testid="participant-name">{name}</span>
        </Tooltip>
        <StatusMarks player={player} asDots={compact} focusTarget={focusTarget} />
        {compact && life}
        <Counters counters={player.counters} focusTarget={focusTarget} />
        {compact && zonesInChip && zones}
      </div>
      {!compact && (
        <div className="participant-head__bottom">
          {life}
          {zones}
        </div>
      )}
    </div>
  );
});

export default ParticipantHead;

function StatusMarks({ player, asDots, focusTarget }) {
  const marks = [
    player.playerStatus === 'eliminated' && { key: 'eliminated', tone: 'danger', label: 'Ausgeschieden' },
    player.isAway && { key: 'away', tone: 'pending', label: 'Abwesend' },
  ].filter(Boolean);
  return marks.map(({ key, tone, label }) => (asDots ? (
    <Tooltip key={key} label={label}>
      <span
        className={`participant-head__status participant-head__status--${tone}`}
        role="img"
        aria-label={label}
        tabIndex={focusTarget}
        data-testid="participant-status"
      />
    </Tooltip>
  ) : (
    <Badge key={key} tone={tone}>{label}</Badge>
  )));
}

function Counters({ counters, focusTarget }) {
  const visible = (counters || []).filter((c) => c.value > 0);
  if (visible.length === 0) return null;
  return (
    <span className="participant-head__counters">
      {visible.map((counter) => {
        const Icon = counterIcon(counter.icon);
        return (
          <Tooltip key={counter.id} label={counter.name}>
            <span
              className="participant-head__counter"
              tabIndex={focusTarget}
              aria-label={`${counter.name} ${counter.value}`}
              data-testid="participant-counter"
              data-counter-id={counter.id}
            >
              <Icon size={14} aria-hidden />
              {counter.value}
            </span>
          </Tooltip>
        );
      })}
    </span>
  );
}

function LifeNumber({ life, name, onOpenValues }) {
  if (!onOpenValues) return <span className="participant-head__life" data-testid="participant-life">{life}</span>;
  return (
    <button
      type="button"
      className="participant-head__life participant-head__life--button"
      data-testid="participant-life"
      aria-label={`Werte von ${name} (Leben ${life})`}
      onClick={onOpenValues}
    >
      {life}
    </button>
  );
}

function OwnLife({ life, name, pendingDelta, onLifeChange, onLongLifeChange, onOpenValues }) {
  return (
    <span className="participant-head__own-life">
      <LifeStepButton step={-1} label="Leben verringern" Icon={Minus} onLifeChange={onLifeChange} onLongLifeChange={onLongLifeChange} />
      <span className="participant-head__life-stack">
        <LifeNumber life={life} name={name} onOpenValues={onOpenValues} />
        {pendingDelta !== 0 && (
          <span className="participant-head__delta" data-testid="participant-life-delta">
            {formatDelta(pendingDelta)}
          </span>
        )}
      </span>
      <LifeStepButton step={1} label="Leben erhöhen" Icon={Plus} onLifeChange={onLifeChange} onLongLifeChange={onLongLifeChange} />
    </span>
  );
}

function LifeStepButton({ step, label, Icon, onLifeChange, onLongLifeChange }) {
  const longPress = useLongPress(() => onLongLifeChange?.(step * LONG_PRESS_STEP));
  return (
    <Tooltip label={`${label}: Klick ${formatDelta(step)}, gedrückt halten ${formatDelta(step * LONG_PRESS_STEP)}`}>
      <button
        type="button"
        className="participant-head__step"
        aria-label={label}
        {...longPress}
        onClick={() => onLifeChange?.(step)}
      >
        <Icon size={18} aria-hidden />
      </button>
    </Tooltip>
  );
}

function ZoneChips({ player, interactive, focusTarget, onZoneClick, onRequestHand, previewBind }) {
  const exileCount = player.exile?.length ?? 0;
  const zoneClick = (zone) => onZoneClick && (() => onZoneClick(zone));
  const openTop = openTopCard(player);
  const zones = [
    { zone: 'hand', count: player.handCount ?? 0, onActivate: onRequestHand },
    {
      zone: 'deck',
      count: player.deck?.length ?? 0,
      highlighted: Boolean(player.deckOpenPlay),
      onActivate: openTop ? zoneClick('deck') : undefined,
      // Fester Schlüssel je Bibliothek: wechselt die Oberkarte unter dem Zeiger, folgt die Vorschau.
      preview: previewBind ? previewBind(openTop, { key: `deck:${player.userId}` }) : null,
    },
    { zone: 'graveyard', count: player.graveyard?.length ?? 0, onActivate: zoneClick('graveyard') },
    exileCount > 0 && { zone: 'exile', count: exileCount, onActivate: zoneClick('exile') },
  ].filter(Boolean);

  return (
    <span className="participant-head__zones">
      {zones.map(({ zone, count, highlighted, onActivate: activate, preview }) => {
        const { label, Icon } = ZONE_SYMBOLS[zone];
        const className = `participant-head__zone${highlighted ? ' participant-head__zone--highlighted' : ''}`;
        const content = (
          <>
            <Icon size={14} aria-hidden />
            {count}
          </>
        );
        if (interactive && activate) {
          return (
            <Tooltip key={zone} label={ZONE_ACTIONS[zone]}>
              <button
                type="button"
                className={className}
                data-testid="participant-zone"
                data-zone={zone}
                aria-label={`${ZONE_ACTIONS[zone]} (${count})`}
                {...(preview || {})}
                onClick={activate}
              >
                {content}
              </button>
            </Tooltip>
          );
        }
        return (
          <Tooltip key={zone} label={`${label}: ${count}`}>
            <span className={className} tabIndex={focusTarget} data-testid="participant-zone" data-zone={zone}>
              {content}
            </span>
          </Tooltip>
        );
      })}
    </span>
  );
}

/** Oberste Karte einer offen gespielten Bibliothek: der Server liefert sie als letztes Element. */
function openTopCard(player) {
  if (!player.deckOpenPlay) return null;
  return player.deck?.[player.deck.length - 1] || null;
}

function formatDelta(delta) {
  return delta > 0 ? `+${delta}` : `−${Math.abs(delta)}`;
}
